import {
  PurchaseOrder,
  PurchaseOrderProductLineItem,
  UpsertPurchaseOrderProductInventoryUnit,
  UpsertPurchaseOrderProductInventoryUnits,
  UpsertPurchaseOrderProductStock,
} from '../../../clients/quipli-service/purchase-order/purchase-order';
import { Modal } from '../../../components/modal/modal';
import { Button } from '../../../components/form/button';
import React, { useCallback, useState } from 'react';
import { usePurchaseOrderApiV1 } from '../../../clients/quipli-service/purchase-order/purchase-order-api-v1';
import { Spinner } from '../../../components/spinner/spinner';
import { toast } from '../../../util/toasts';
import { ProductTrackingType, ProductType } from '../../../clients/quipli-service/product/product';
import { Toggle } from '../../../components/form/toggle';

function initStockUpdate(pop: PurchaseOrderProductLineItem): UpsertPurchaseOrderProductStock {
  if (pop.product.allowSale && !pop.product.allowRent) {
    return {
      purchaseOrderProductId: pop.id,
      saleQuantity: pop.quantity,
      rentalQuantity: 0,
    };
  }
  if (pop.product.allowRent && !pop.product.allowSale) {
    return {
      purchaseOrderProductId: pop.id,
      saleQuantity: 0,
      rentalQuantity: pop.quantity,
    };
  }
  return {
    purchaseOrderProductId: pop.id,
    saleQuantity: pop.stock?.saleQuantity || 0,
    rentalQuantity: pop.stock?.rentalQuantity || 0,
  };
}

function initUnitUpdate(pop: PurchaseOrderProductLineItem): UpsertPurchaseOrderProductInventoryUnits {
  const units = [];
  for (let i = 0; i < pop.quantity; i++) {
    units.push({
      unitNumber: '',
      serialNumber: '',
      forRent: pop.product.allowRent && !pop.product.allowSale,
      forSale: pop.product.allowSale && !pop.product.allowRent,
    });
  }
  return {
    purchaseOrderProductId: pop.id,
    units,
  };
}

export const ReceivePurchaseOrder = ({
  purchaseOrder,
  onCancel,
  onComplete,
}: {
  purchaseOrder: PurchaseOrder;
  onCancel: () => void;
  onComplete: () => void;
}) => {
  const { receivePurchaseOrder } = usePurchaseOrderApiV1();
  const receivableProducts = purchaseOrder.products.filter((pop) => pop.product.type !== ProductType.Custom);

  const [isReceiving, setIsReceiving] = useState<boolean>(false);
  const [productIndex, setProductIndex] = useState<number>(0);
  const [unitIndex, setUnitIndex] = useState<number>(0);
  const [stockUpdates, setStockUpdates] = useState<UpsertPurchaseOrderProductStock[]>(
    receivableProducts.filter((pop) => pop.product.trackingType === ProductTrackingType.Quantity).map(initStockUpdate),
  );
  const [unitUpdates, setUnitUpdates] = useState<UpsertPurchaseOrderProductInventoryUnits[]>(
    receivableProducts
      .filter((pop) => pop.product.trackingType === ProductTrackingType.SerialUnits)
      .map(initUnitUpdate),
  );

  const doReceive = async () => {
    setIsReceiving(true);
    try {
      await receivePurchaseOrder(purchaseOrder.id, { products: [...stockUpdates, ...unitUpdates] });
      onComplete();
    } catch (e: any) {
      toast(e.message || 'Failed to receive purchase order', 'error');
      setIsReceiving(false);
    }
  };

  let purchaseOrderProduct: PurchaseOrderProductLineItem | undefined = undefined;
  let stock = undefined;
  let unitUpdate = undefined;
  if (productIndex < receivableProducts.length) {
    purchaseOrderProduct = receivableProducts[productIndex];
    if (purchaseOrderProduct.product.trackingType === ProductTrackingType.Quantity) {
      stock = stockUpdates.find((s) => s.purchaseOrderProductId === purchaseOrderProduct!.id)!;
    } else {
      unitUpdate = unitUpdates.find((s) => s.purchaseOrderProductId === purchaseOrderProduct!.id)!;
    }
  }

  const showBack = productIndex > 0 || unitIndex > 0;
  const showNext = productIndex < receivableProducts.length;
  const showReceive = productIndex === receivableProducts.length;
  const canReceive = [
    ...stockUpdates.map((s) =>
      validateStock(receivableProducts.find((pop) => pop.id === s.purchaseOrderProductId)!, s),
    ),
    ...unitUpdates.flatMap((uus) => uus.units).map((u) => validateUnit(u)),
  ].every((errs) => errs.length === 0);

  const onNext = useCallback(() => {
    if (
      purchaseOrderProduct!.product.trackingType === ProductTrackingType.Quantity ||
      unitIndex === purchaseOrderProduct!.quantity - 1
    ) {
      setProductIndex((v) => v + 1);
      setUnitIndex(0);
    } else {
      setUnitIndex((v) => v + 1);
    }
  }, [unitIndex]);

  const onBack = useCallback(() => {
    if (
      !purchaseOrderProduct ||
      purchaseOrderProduct.product.trackingType === ProductTrackingType.Quantity ||
      unitIndex === 0
    ) {
      setProductIndex((v) => v - 1);
      const prevProduct = receivableProducts[productIndex - 1];
      if (prevProduct.product.trackingType === ProductTrackingType.SerialUnits) {
        setUnitIndex(prevProduct.quantity - 1);
      }
    } else {
      setUnitIndex((v) => v - 1);
    }
  }, [unitIndex, productIndex]);

  return (
    <Modal
      isOpen={true}
      title="Receive Purchase Order"
      actions={
        <>
          <Button className="button button-secondary" disabled={isReceiving} onClick={() => onCancel()}>
            Cancel
          </Button>
          {showBack && (
            <Button dataCy="approve-purchase-order-modal" className="button button-secondary" onClick={onBack}>
              Back
            </Button>
          )}
          {showNext && (
            <Button dataCy="approve-purchase-order-modal" className="button button-yellow" onClick={onNext}>
              Next
            </Button>
          )}
          {showReceive && (
            <Button
              dataCy="approve-purchase-order-modal"
              className="button button-yellow"
              disabled={isReceiving || !canReceive}
              onClick={() => doReceive()}
            >
              {isReceiving ? <Spinner /> : 'Receive'}
            </Button>
          )}
        </>
      }
      close={() => !isReceiving && onCancel()}
    >
      {purchaseOrderProduct &&
        (purchaseOrderProduct.product.trackingType === ProductTrackingType.Quantity ? (
          <QuantityProductForm
            purchaseOrderProduct={purchaseOrderProduct}
            stock={stock!}
            onUpdate={(update) =>
              setStockUpdates((sus) =>
                sus.map((sus) => {
                  if (sus.purchaseOrderProductId === update.purchaseOrderProductId) {
                    return update;
                  } else {
                    return sus;
                  }
                }),
              )
            }
          />
        ) : (
          <ProductUnitForm
            purchaseOrderProduct={purchaseOrderProduct}
            units={unitUpdate!.units}
            onUpdate={(update) =>
              setUnitUpdates((sus) =>
                sus.map((sus) => {
                  if (sus.purchaseOrderProductId === purchaseOrderProduct!.id) {
                    return { purchaseOrderProductId: purchaseOrderProduct!.id, units: update };
                  } else {
                    return sus;
                  }
                }),
              )
            }
            index={unitIndex}
          />
        ))}
      {productIndex === receivableProducts.length && (
        <ReceiveSummary receivableProducts={receivableProducts} stocks={stockUpdates} units={unitUpdates} />
      )}
    </Modal>
  );
};

const QuantityProductForm = ({
  purchaseOrderProduct,
  stock,
  onUpdate,
}: {
  purchaseOrderProduct: PurchaseOrderProductLineItem;
  stock: UpsertPurchaseOrderProductStock;
  onUpdate: (stock: UpsertPurchaseOrderProductStock) => void;
}) => {
  return (
    <div className="flex flex-column gap-2 text-gray-700">
      <div className="flex gap-2 items-center text-lg">
        <div className="flex gap-2 w-1/2">
          <label>Product:</label>
          <span className="text-gray-500">{purchaseOrderProduct.product.name}</span>
        </div>
        <div className="flex gap-2 w-1/2">
          <label>Quantity:</label>
          <span className="text-gray-500">{purchaseOrderProduct.quantity}</span>
        </div>
      </div>
      {purchaseOrderProduct.product.allowSale && (
        <div className="flex gap-2 items-center">
          <label className="w-1/2 text-gray-700">Sale Quantity</label>
          <input
            className="w-1/2"
            type="number"
            min={0}
            value={stock.saleQuantity}
            disabled={!purchaseOrderProduct.product.allowRent}
            onChange={(v) => onUpdate({ ...stock, saleQuantity: parseFloat(v.target.value) })}
          />
        </div>
      )}
      {purchaseOrderProduct.product.allowRent && (
        <div className="flex gap-2 items-center">
          <label className="w-1/2 ">Rental Quantity</label>
          <input
            className="w-1/2"
            type="number"
            min={0}
            value={stock.rentalQuantity}
            disabled={!purchaseOrderProduct.product.allowSale}
            onChange={(v) => onUpdate({ ...stock, rentalQuantity: parseInt(v.target.value) })}
          />
        </div>
      )}
    </div>
  );
};

const ProductUnitForm = ({
  purchaseOrderProduct,
  units,
  onUpdate,
  index,
}: {
  purchaseOrderProduct: PurchaseOrderProductLineItem;
  units: UpsertPurchaseOrderProductInventoryUnit[];
  onUpdate: (units: UpsertPurchaseOrderProductInventoryUnit[]) => void;
  index: number;
}) => {
  const unit = units[index];
  const updateUnit = (u: UpsertPurchaseOrderProductInventoryUnit) => {
    onUpdate(
      units.map((o, i) => {
        if (i === index) {
          return u;
        } else {
          return o;
        }
      }),
    );
  };
  return (
    <div className="flex flex-column gap-2 text-gray-700">
      <div className="flex gap-2 items-center text-lg">
        <div className="flex gap-2 w-1/2">
          <label>Product:</label>
          <span className="text-gray-500">{purchaseOrderProduct.product.name}</span>
        </div>
        <div className="flex gap-2 w-1/2">
          <label>Unit:</label>
          <span className="text-gray-500">
            {index + 1} of {purchaseOrderProduct.quantity}
          </span>
        </div>
      </div>
      <div className="flex gap-2 items-center">
        <label className="w-1/2 text-gray-700">Unit Number</label>
        <input
          className="w-1/2"
          type="text"
          value={unit.unitNumber}
          onChange={(e) => updateUnit({ ...unit, unitNumber: e.target.value })}
        />
      </div>
      <div className="flex gap-2 items-center">
        <label className="w-1/2 text-gray-700">Serial Number</label>
        <input
          className="w-1/2"
          type="text"
          value={unit.serialNumber}
          onChange={(e) => updateUnit({ ...unit, serialNumber: e.target.value })}
        />
      </div>
      <div className="flex gap-2 items-center">
        <label className="w-1/2 text-gray-700">QR Code</label>
        <input
          className="w-1/2"
          type="text"
          value={unit.qrCode || ''}
          onChange={(e) => updateUnit({ ...unit, qrCode: e.target.value })}
        />
      </div>
      <div className="flex gap-2 items-center">
        <label className="w-1/2 text-gray-700">Machine Hours</label>
        <input
          className="w-1/2"
          type="number"
          value={unit.hourReading || ''}
          onChange={(e) => updateUnit({ ...unit, hourReading: parseFloat(e.target.value) })}
        />
      </div>
      {purchaseOrderProduct.product.allowSale && purchaseOrderProduct.product.allowRent && (
        <>
          <div className="flex gap-2 items-center">
            <label className="w-1/2 text-gray-700">For Sale</label>
            <Toggle onClick={() => updateUnit({ ...unit, forSale: !unit.forSale })} checked={unit.forSale} />
          </div>
          <div className="flex gap-2 items-center">
            <label className="w-1/2 text-gray-700">For Rent</label>
            <Toggle onClick={() => updateUnit({ ...unit, forRent: !unit.forRent })} checked={unit.forRent} />
          </div>
        </>
      )}
    </div>
  );
};

const ReceiveSummary = ({
  receivableProducts,
  units,
  stocks,
}: {
  receivableProducts: PurchaseOrderProductLineItem[];
  units: UpsertPurchaseOrderProductInventoryUnits[];
  stocks: UpsertPurchaseOrderProductStock[];
}) => {
  return (
    <div>
      <ul className="list-none flex flex-column p-0 gap-4">
        {receivableProducts.map((pop) => {
          if (pop.product.trackingType === ProductTrackingType.Quantity) {
            const stock = stocks.find((s) => s.purchaseOrderProductId === pop.id)!;
            return (
              <li key={pop.id} className="flex flex-column">
                <h6 className="flex text-lg gap-1 text-grey-700 text-lg m-0">
                  <span>Product:</span>
                  <span>{pop.product.name}</span>
                </h6>
                <ul className="list-none pl-4">
                  <li className="flex flex-row gap-1">
                    <span className="text-grey-700">Sale Quantity:</span>
                    <span>{stock.saleQuantity}</span>
                  </li>
                  <li className="flex flex-row gap-1">
                    <span className="text-grey-700">Rental Quantity:</span>
                    <span>{stock.rentalQuantity}</span>
                  </li>
                  {validateStock(pop, stock).map((err) => (
                    <li className="text-error-400">{err}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            const us = units.find((u) => u.purchaseOrderProductId === pop.id)!;
            return (
              <li key={pop.id} className="flex flex-column">
                <h6 className="flex text-lg gap-1 text-grey-700 text-lg m-0">
                  <span>Product:</span>
                  <span>{pop.product.name}</span>
                </h6>
                <ul className="list-none flex flex-column p-0 gap-4 text-grey-500">
                  {us.units.map((u, i) => (
                    <li key={i} className="flex flex-column">
                      <ul className="list-none pl-4">
                        <li className="flex flex-row gap-1">
                          <span className="text-grey-700">Unit Number:</span>
                          <span>{u.unitNumber}</span>
                        </li>
                        <li className="flex flex-row gap-1">
                          <span className="text-grey-700">Serial Number:</span>
                          <span>{u.serialNumber}</span>
                        </li>
                        <li className="flex flex-row gap-1">
                          <span className="text-grey-700">QR Code:</span>
                          <span>{u.qrCode}</span>
                        </li>
                        <li className="flex flex-row gap-1">
                          <span className="text-grey-700">Machine Hours:</span>
                          <span>{u.hourReading}</span>
                        </li>
                        <li className="flex flex-row gap-1">
                          <span className="text-grey-700">For Sale:</span>
                          <span>{u.forSale ? 'Yes' : 'No'}</span>
                        </li>
                        <li className="flex flex-row gap-1">
                          <span className="text-grey-700">For Rent:</span>
                          <span>{u.forRent ? 'Yes' : 'No'}</span>
                        </li>
                        {validateUnit(u).map((err) => (
                          <li className="text-error-400">{err}</li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              </li>
            );
          }
        })}
      </ul>
    </div>
  );
};

function validateStock(pop: PurchaseOrderProductLineItem, stock: UpsertPurchaseOrderProductStock): string[] {
  const errors = [];
  if (pop.quantity !== stock.rentalQuantity + stock.saleQuantity) {
    errors.push('Sale Quantity + Rental Quantity do not equal order quantity');
  }
  return errors;
}

function validateUnit(u: UpsertPurchaseOrderProductInventoryUnit): string[] {
  const errors = [];
  if (!u.unitNumber) {
    errors.push('Unit number is required');
  }
  if (!u.serialNumber) {
    errors.push('Serial number is required');
  }
  if (!u.forSale || !u.forRent) {
    errors.push('Unit must be listed as for sale or rent');
  }
  return errors;
}
